/**
 * Admin billing worklist, side by side with each encounter's event stream
 * (GDN-2774; backend GDN-2772 / #3905).
 *
 * Rendered as the `billing-worklist` section of the organization workspace, not
 * as a page of its own. It first shipped as a standalone route, which meant
 * opening it dropped the org rail for the global one — an org-scoped surface
 * that looked like it had left the org. Being a section means the workspace
 * chrome, context stack and rail selection are simply never lost; the old
 * route redirects here. Filters, paging and expansion stay in the query string,
 * alongside the shell's own `?tab=`, so every state is still deep-linkable.
 *
 * This is a measurement instrument, not an operator tool. The worklist asserts
 * a state for every encounter — a next step, a stage, a work state — and those
 * assertions are computed from table reads. The event stream is what the
 * system actually recorded. Putting them on the same row turns "how good is
 * our event coverage?" from a query into a glance: any row badged **No events
 * recorded** is a state we can assert but cannot explain from events.
 *
 * So the empty case is the feature. Rows with no events are tinted, badged in
 * red, counted in a banner at the top, and can be isolated with a filter. A
 * row with events is deliberately quiet.
 *
 * PHI-gated: Admin or `staff:phi_read`. A plain Moderator gets 403
 * `phi_access_denied`, which this page names rather than swallowing.
 *
 * Admin surfaces are exempt from Statsig instrumentation — no event tracking
 * here by design.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  EncounterEventCoverageChip,
  EncounterEventStream,
  EventLensToggle,
  type CoverageState,
} from '../components/encounter-event-stream';
import type { EventLens } from '../hooks/useEncounterEvents';
import { useGetAdminOrganizationWorklistQuery } from '../store/hooks';
import type { WorklistItem } from '../store/api';
import { isPhiAccessDenied } from '../utils/phi-access';

const DEFAULT_PER_PAGE = 25;

// Every union below is read off the generated worklist schema rather than
// re-declared, so a backend vocabulary change breaks the build here instead of
// silently rendering a raw enum value.
type NextStep = NonNullable<WorklistItem['next_step']>;
type WorkState = WorklistItem['signal']['work_state'];
type Stage = WorklistItem['flow']['active_stage'];
type Severity = WorklistItem['signal']['severity'];

/**
 * Operator-facing labels. Typed as a total Record over the generated unions,
 * which is what makes them exhaustive: adding a `next_step` server-side fails
 * typecheck here until it gets a label.
 */
const NEXT_STEP_LABELS: Record<NextStep, string> = {
  monitor_visit: 'Monitor visit',
  verify_coverage: 'Verify coverage',
  sign_note: 'Sign note',
  create_claim: 'Create claim',
  review_claim: 'Review claim',
  track_payer: 'Track payer',
  send_invoice: 'Send invoice',
  collect_balance: 'Collect balance',
  track_settlement: 'Track settlement',
  done: 'Done',
};

const WORK_STATE_LABELS: Record<WorkState, string> = {
  all: 'All',
  action: 'Action',
  waiting: 'Waiting',
  clear: 'Clear',
};

const STAGE_LABELS: Record<Stage, string> = {
  schedule: 'Schedule',
  support: 'Support',
  sign: 'Sign',
  submit: 'Submit',
  settle: 'Settle',
};

const SEVERITY_COLORS: Record<Severity, 'info' | 'warning' | 'error' | 'success'> = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  success: 'success',
};

const WORK_STATE_COLORS: Record<WorkState, 'default' | 'error' | 'warning' | 'success'> = {
  all: 'default',
  action: 'error',
  waiting: 'warning',
  clear: 'success',
};

const NEXT_STEP_VALUES = Object.keys(NEXT_STEP_LABELS) as NextStep[];
// 'all' is the absence of a work-state filter, so it isn't offered as one.
const WORK_STATE_VALUES = (Object.keys(WORK_STATE_LABELS) as WorkState[]).filter(
  value => value !== 'all',
);
const STAGE_VALUES = Object.keys(STAGE_LABELS) as Stage[];

const LENS_VALUES = new Set<EventLens>(['all', 'domain', 'audit', 'usage']);

const asNextStep = (raw: string | null): NextStep | undefined =>
  raw && (NEXT_STEP_VALUES as string[]).includes(raw) ? (raw as NextStep) : undefined;

const asWorkState = (raw: string | null): WorkState | undefined =>
  raw && (WORK_STATE_VALUES as string[]).includes(raw) ? (raw as WorkState) : undefined;

const asStage = (raw: string | null): Stage | undefined =>
  raw && (STAGE_VALUES as string[]).includes(raw) ? (raw as Stage) : undefined;

const asLens = (raw: string | null): EventLens =>
  raw && LENS_VALUES.has(raw as EventLens) ? (raw as EventLens) : 'domain';

const claimLabel = (item: WorklistItem): string => {
  const { status, claim_type: claimType } = item.claim;
  if (!status && !claimType) return 'No claim';
  return [claimType, status].filter(Boolean).join(' · ');
};

/**
 * Query-string keys this section owns. The workspace shell shares one query
 * string across all its sections, so it needs to know which keys to drop when
 * you navigate away — otherwise a `?stage=` set here would leak into whatever
 * section reads that name next.
 */
export const BILLING_WORKLIST_PARAMS = [
  'page',
  'next_step',
  'work_state',
  'stage',
  'lens',
  'gaps_only',
] as const;

export interface BillingWorklistProps {
  /**
   * Organization whose worklist this is. Always supplied by the workspace
   * shell; the worklist has no meaning without it and says so.
   */
  organizationId: string;
}

export default function BillingWorklist({ organizationId }: BillingWorklistProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Per-row coverage, reported up by each row's badge. The banner is the whole
  // point of the page, and it can only be counted once every row has answered.
  //
  // The map is stamped with the query it describes rather than cleared by an
  // effect when that query changes. Clearing can't work: child effects run
  // before the parent's, so a reset effect wipes the very reports it was
  // meant to make room for, and the banner sits at "0 of N checked" forever.
  // Stamping makes staleness a read-time comparison instead.
  const [coverage, setCoverage] = useState<{
    signature: string;
    states: Record<string, CoverageState>;
  }>({ signature: '', states: {} });

  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);
  const nextStep = asNextStep(searchParams.get('next_step'));
  const workState = asWorkState(searchParams.get('work_state'));
  const stage = asStage(searchParams.get('stage'));
  const lens = asLens(searchParams.get('lens'));
  const gapsOnly = searchParams.get('gaps_only') === '1';

  const patch = useCallback(
    (changes: Record<string, string | null>) => {
      setSearchParams(previous => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(changes)) {
          if (value === null || value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const {
    data,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetAdminOrganizationWorklistQuery(
    {
      organizationId,
      page,
      perPage: DEFAULT_PER_PAGE,
      nextStep,
      workState,
      stage,
    },
    { skip: !organizationId },
  );

  const items = useMemo(() => data?.items ?? [], [data]);

  // Identifies the exact question the rows are answering. Any change to it
  // retires every previous answer.
  const signature = `${organizationId}|${page}|${lens}|${nextStep ?? ''}|${workState ?? ''}|${stage ?? ''}`;

  // Re-created when the signature changes, which is deliberate: the new
  // identity re-runs each badge's report effect, so rows re-answer under the
  // new question without an explicit invalidation pass.
  const onCoverageResolved = useCallback(
    (encounterId: string, state: CoverageState) => {
      setCoverage(previous => {
        if (previous.signature !== signature) {
          return { signature, states: { [encounterId]: state } };
        }
        if (previous.states[encounterId] === state) return previous;
        return { signature, states: { ...previous.states, [encounterId]: state } };
      });
    },
    [signature],
  );

  // Expansion is scoped to the rows on screen; a new query means new rows.
  useEffect(() => {
    setExpanded(new Set());
  }, [signature]);

  const toggleRow = useCallback((rowId: string) => {
    setExpanded(previous => {
      const next = new Set(previous);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  // Reports stamped with a superseded question count for nothing.
  const states = coverage.signature === signature ? coverage.states : {};
  const resolvedCount = items.filter(item => {
    const state = states[item.encounter_id];
    return state && state !== 'loading';
  }).length;
  const gapCount = items.filter(item => states[item.encounter_id] === 'empty').length;
  const allResolved = items.length > 0 && resolvedCount === items.length;

  // "Gaps only" hides rows it has *proved* are covered, not rows it hasn't
  // asked about yet. The probe lives inside the row, so filtering on an
  // unresolved row would unmount the thing that resolves it and the list would
  // never fill in. Rows drop out as they come back covered.
  const visibleItems = gapsOnly
    ? items.filter(item => {
        const state = states[item.encounter_id];
        return state === undefined || state === 'loading' || state === 'empty';
      })
    : items;

  if (!organizationId) {
    return (
      <Alert severity="error">
        The billing worklist is always org-scoped; this URL has no organization.
      </Alert>
    );
  }

  // The PHI gate is a distinct, actionable outcome — never a generic error.
  if (isPhiAccessDenied(error)) {
    return (
      <Alert severity="warning" data-testid="phi-access-denied">
        <AlertTitle>Requires admin PHI access</AlertTitle>
        The billing worklist exposes patient and provider identity, so it is limited to Admin and
        staff with <code>staff:phi_read</code>. Your account can reach admin tooling but not PHI —
        ask an administrator to grant PHI read access if you need this view.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Worklist &amp; events
        </Typography>
        <IconButton
          aria-label="Reload worklist"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          {isFetching ? <CircularProgress size={18} /> : <RefreshIcon />}
        </IconButton>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Each row is an encounter the worklist places in a concrete state. Expand it to see what the
        system actually recorded. Rows with no events are coverage gaps.
      </Typography>

      {/* The headline measurement. */}
      {items.length > 0 && (
        <Alert
          severity={gapCount > 0 ? 'error' : 'success'}
          icon={gapCount > 0 ? <ErrorOutlineIcon /> : undefined}
          sx={{ mb: 2 }}
          data-testid="coverage-banner"
        >
          {allResolved ? (
            <>
              <strong>
                {gapCount} of {items.length}
              </strong>{' '}
              rows on this page have no recorded {lens === 'all' ? '' : `${lens} `}events.
            </>
          ) : (
            <>
              Measuring event coverage… {resolvedCount} of {items.length} rows checked
              {gapCount > 0 ? `, ${gapCount} gap${gapCount === 1 ? '' : 's'} so far` : ''}.
            </>
          )}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Next step"
            value={nextStep ?? ''}
            onChange={event => patch({ next_step: event.target.value || null, page: '1' })}
            sx={{ minWidth: 180 }}
            slotProps={{ htmlInput: { 'aria-label': 'Next step filter' } }}
          >
            <MenuItem value="">Any next step</MenuItem>
            {NEXT_STEP_VALUES.map(value => (
              <MenuItem key={value} value={value}>
                {NEXT_STEP_LABELS[value]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Work state"
            value={workState ?? ''}
            onChange={event => patch({ work_state: event.target.value || null, page: '1' })}
            sx={{ minWidth: 160 }}
            slotProps={{ htmlInput: { 'aria-label': 'Work state filter' } }}
          >
            <MenuItem value="">Any work state</MenuItem>
            {WORK_STATE_VALUES.map(value => (
              <MenuItem key={value} value={value}>
                {WORK_STATE_LABELS[value]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Stage"
            value={stage ?? ''}
            onChange={event => patch({ stage: event.target.value || null, page: '1' })}
            sx={{ minWidth: 150 }}
            slotProps={{ htmlInput: { 'aria-label': 'Stage filter' } }}
          >
            <MenuItem value="">Any stage</MenuItem>
            {STAGE_VALUES.map(value => (
              <MenuItem key={value} value={value}>
                {STAGE_LABELS[value]}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          <FormControlLabel
            control={
              <Switch
                checked={gapsOnly}
                onChange={event => patch({ gaps_only: event.target.checked ? '1' : null })}
              />
            }
            label="Gaps only"
          />
          <EventLensToggle lens={lens} onChange={next => patch({ lens: next })} />
        </Box>
      </Paper>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && error && (
        <Alert severity="error">
          The worklist could not be loaded. This is a request failure, not a coverage result.
        </Alert>
      )}

      {!isLoading && !error && (
        <>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="Billing worklist">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Patient / Provider</TableCell>
                  <TableCell>Next step</TableCell>
                  <TableCell>Stage</TableCell>
                  <TableCell>Work state</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell>Claim</TableCell>
                  <TableCell>Events</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleItems.map(item => {
                  const isOpen = expanded.has(item.id);
                  const isGap = states[item.encounter_id] === 'empty';
                  return (
                    <Fragment key={item.id}>
                      <TableRow
                        hover
                        onClick={() => toggleRow(item.id)}
                        sx={{
                          cursor: 'pointer',
                          // The gap is visible before you expand anything.
                          ...(isGap && {
                            bgcolor: 'error.lighter',
                            borderLeft: 4,
                            borderColor: 'error.main',
                          }),
                        }}
                        data-testid={isGap ? 'worklist-row-gap' : 'worklist-row'}
                      >
                        <TableCell padding="checkbox">
                          <IconButton size="small" aria-label={isOpen ? 'Collapse' : 'Expand'}>
                            {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.identity.patient_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.identity.provider_name}
                            {item.identity.service_label ? ` · ${item.identity.service_label}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.next_step ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={NEXT_STEP_LABELS[item.next_step]}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{STAGE_LABELS[item.flow.active_stage]}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={WORK_STATE_COLORS[item.signal.work_state]}
                            variant="outlined"
                            label={WORK_STATE_LABELS[item.signal.work_state]}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={SEVERITY_COLORS[item.signal.severity]}
                            variant="outlined"
                            label={item.signal.severity}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.age.label}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{claimLabel(item)}</Typography>
                        </TableCell>
                        <TableCell>
                          <EncounterEventCoverageChip
                            organizationId={organizationId}
                            encounterId={item.encounter_id}
                            lens={lens}
                            onResolve={onCoverageResolved}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}
                        >
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, py: 1 }}>
                              <Typography variant="overline" color="text.secondary">
                                Event stream · encounter {item.encounter_id}
                              </Typography>
                              <EncounterEventStream
                                organizationId={organizationId}
                                encounterId={item.encounter_id}
                                lens={lens}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
                {visibleItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        {gapsOnly && items.length > 0
                          ? 'Every row on this page has recorded events.'
                          : 'No worklist rows match these filters.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 2 }}
          >
            <Button
              size="small"
              disabled={page <= 1 || isFetching}
              onClick={() => patch({ page: String(page - 1) })}
            >
              Previous
            </Button>
            <Typography variant="body2" color="text.secondary">
              Page {page} · {items.length} row{items.length === 1 ? '' : 's'}
            </Typography>
            <Button
              size="small"
              disabled={!data?.has_more || isFetching}
              onClick={() => patch({ page: String(page + 1) })}
            >
              Next
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
